import { useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase/client'
import type { TripCrewMessage } from '@/shared/api/types'

interface UseRealtimeTripChatProps {
  tripId: string
  enabled: boolean
  onNewMessage: (message: TripCrewMessage) => void
  onUpdatedMessage: (message: TripCrewMessage) => void
  onDeletedMessage: (messageId: string) => void
  onTypingChange: (userId: string, isTyping: boolean) => void
}

export function useRealtimeTripChat({
  tripId,
  enabled,
  onNewMessage,
  onUpdatedMessage,
  onDeletedMessage,
  onTypingChange,
}: UseRealtimeTripChatProps) {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)

  useEffect(() => {
    if (!enabled || !tripId) return

    // Public broadcast channel — secured by trip UUID in the name.
    // Backend broadcasts message_created / message_updated / message_deleted events.
    // Typing indicators are sent directly by clients via the same channel.
    // Switch to { config: { private: true } } once migration 117 + Realtime
    // Authorization are confirmed active in the Supabase dashboard.
    const channel = supabase
      .channel(`trip:${tripId}:messages`)
      .on('broadcast', { event: 'message_created' }, ({ payload }) => {
        if (payload && typeof payload === 'object' && 'id' in payload) {
          onNewMessage(payload as TripCrewMessage)
        }
      })
      .on('broadcast', { event: 'message_updated' }, ({ payload }) => {
        if (payload && typeof payload === 'object' && 'id' in payload) {
          onUpdatedMessage(payload as TripCrewMessage)
        }
      })
      .on('broadcast', { event: 'message_deleted' }, ({ payload }) => {
        if (payload && typeof payload === 'object' && 'id' in payload) {
          onDeletedMessage((payload as { id: string }).id)
        }
      })
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (
          payload &&
          typeof payload.userId === 'string' &&
          typeof payload.isTyping === 'boolean'
        ) {
          onTypingChange(payload.userId, payload.isTyping)
        }
      })
      .subscribe()

    channelRef.current = channel

    return () => {
      channel.unsubscribe()
      channelRef.current = null
    }
  }, [tripId, enabled, onNewMessage, onUpdatedMessage, onDeletedMessage, onTypingChange])

  const broadcastTyping = (isTyping: boolean) => {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'typing',
      payload: { isTyping },
    })
  }

  return { broadcastTyping }
}
